import { useParams } from 'react-router-dom';
import {
  fetchAgentList,
  fetchAgentLogs,
  fetchBrandsForAgents,
  fetchMismatchRequests,
} from '../hooks/queries';
import { useEffect, useState, useMemo } from 'react';
import { LoadingErrorState } from '../components/LoadingErrorState';
import { useCalenderState } from '../state/calender-state';
import toast from 'react-hot-toast';
import InvoiceHeader from '../components/InvoiceHeader';
import { BtnPrimary } from '../components/common/Buttons';
import CalenderView from '../components/common/CalenderView';
import Modal from '../components/common/Modal';
import TimeEntry, { getDefaultValues } from '../components/forms/TimeEntry';
import GenerateInvoice from '../components/forms/GenerateInvoice';
import { ITimeEntry } from '../types/timeentry';
import moment from 'moment-timezone';

const Invoice = () => {
  const { id } = useParams();

  if (!id) return null;

  const { currentDate, selectedTimezoneName } = useCalenderState();

  const [selectedBrand, setSelectedBrand] = useState<string | undefined>(
    undefined
  );

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [onGenerateInvoiceModalOpen, setOnGenerateInvoiceModalOpen] =
    useState(false);

  const { data: agents, isSuccess: isAgentsSuccess } = fetchAgentList();

  const { data: brandsForAgents, isSuccess: isBrandsForAgentsSuccess } =
    fetchBrandsForAgents();

  const offsetInMinutes = useMemo(() => {
    return moment.tz(selectedTimezoneName).utcOffset();
  }, [selectedTimezoneName]);

  const agentLogsParams = {
    agentId: id,
    account: selectedBrand!,
    month: (currentDate.getMonth() + 1).toString().padStart(2, '0'),
    year: currentDate.getFullYear().toString(),
    offset: offsetInMinutes,
  };

  const mismatchParams = {
    agentId: id,
    account: selectedBrand!,
    date: currentDate.toISOString(),
  };

  const {
    data: agentLogs,
    isLoading: isAgentLogsLoading,
    isFetching: isAgentLogsFetching,
    error: agentLogsError,
    refetch: refetchAgentLogs,
  } = fetchAgentLogs({
    ...agentLogsParams,
    enabled: !!selectedBrand,
  });

  const { data: mismatchRequestsData, refetch: refetchMismatchRequests } =
    fetchMismatchRequests({
      ...mismatchParams,
      enabled: !!selectedBrand,
    });

  useEffect(() => {
    if (brandsForAgents && id && isBrandsForAgentsSuccess && !selectedBrand) {
      setSelectedBrand(brandsForAgents?.[id]?.[0].alias);
    }
  }, [brandsForAgents, id, isBrandsForAgentsSuccess, selectedBrand]);

  const agent = agents?.agents?.find((agent) => agent.kimId === id);

  if (
    !agent ||
    !isAgentsSuccess ||
    !brandsForAgents ||
    !isBrandsForAgentsSuccess
  )
    return null;

  const brands = brandsForAgents[id];

  const isLogsLoaded =
    !isAgentLogsLoading && !isAgentLogsFetching && !agentLogsError && agentLogs;

  const openMismatchModal = (date?: Date) => {
    if (date) {
      setSelectedDate(date);
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDate(null);
  };
  const refetch = () => {
    Promise.all([
      refetchAgentLogs().catch(() =>
        toast.error('Error refetching mismatch requests')
      ),
      refetchMismatchRequests().catch(() =>
        toast.error('Error refetching mismatch requests')
      ),
    ]);
  };

  const onTimeEntryEdit = () => {
    toast.success('Worklog updated successfully!');
    refetch();
  };

  const onTimeEntryAdd = () => {
    toast.success('Worklog added successfully!');
    refetch();
    closeModal();
  };

  const onTimeEntryDelete = (entries?: ITimeEntry[]) => {
    toast.success('Worklog deleted successfully!');
    refetch();
    if (entries?.length === 0) closeModal();
  };

  const defaultValues = getDefaultValues(
    agentLogs?.data ?? [],
    selectedDate,
    selectedTimezoneName
  );

  const selectedBrandForAgent = () => {
    const agentBrands = brandsForAgents?.[id];
    return agentBrands?.find((brand) => brand.alias === selectedBrand);
  };

  return (
    <div
      className="w-full space-y-8"
      style={{
        height: 'calc(100vh - 3rem)',
      }}
    >
      <InvoiceHeader
        agentName={agent.name}
        defaultBrand={brands?.[0]?.alias}
        brands={brands}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        hideDateSelector={!isLogsLoaded}
      />
      <LoadingErrorState
        isLoading={isAgentLogsLoading}
        isFetching={isAgentLogsFetching}
        error={agentLogsError}
        refetch={refetchAgentLogs}
        data={agentLogs}
      />
      {isLogsLoaded && (
        <div className="h-full flex flex-col space-y-8">
          <CalenderView
            {...agentLogsParams}
            onCellClick={openMismatchModal}
            mismatchRequests={mismatchRequestsData?.data}
          />
          <BtnPrimary
            onClick={() => setOnGenerateInvoiceModalOpen(true)}
            className="self-end"
          >
            Generate Invoice
          </BtnPrimary>
        </div>
      )}

      <Modal
        isOpen={onGenerateInvoiceModalOpen}
        onRequestClose={() => setOnGenerateInvoiceModalOpen(false)}
        overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        width="40%"
        title="Generate Invoice"
      >
        <GenerateInvoice
          {...agentLogsParams}
          selectedBrand={selectedBrandForAgent()}
        />
      </Modal>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        width="40%"
        title={defaultValues?.length ? 'Edit Worklog' : 'Add Worklog'}
      >
        <TimeEntry
          {...agentLogsParams}
          selectedDate={selectedDate}
          onEditSuccess={onTimeEntryEdit}
          onAddSuccess={onTimeEntryAdd}
          onDeleteSuccess={onTimeEntryDelete}
        />
      </Modal>
    </div>
  );
};

export default Invoice;
